import React, { useEffect, useMemo, useState } from "react";

import { Localized, useLocalization } from "@fluent/react";
import { useDocumentFilters } from "byzantine/src/dbbl/businessLogic/entities/documents/hooks";
import { useAccountDocuments } from "byzantine/src/dbbl/businessLogic/entities/documents/requests";
import {
  ContentCard,
  LoadingShim,
  Pagination,
  Row,
  useBreakpoints,
} from "@narmi/design_system";
import cc from "classcat";
import { useAccounts } from "byzantine/src/dbbl/businessLogic/entities/accounts";

import utils from "../../../utils";
import PageLayout from "../../components/PageLayout";
import { useDragHorizontalScroll } from "../../../components/hooks/useDragHorizontalScroll";

import DocumentFilterAnchoredDialog from "./DocumentFilterAnchoredDialog/DocumentFilterAnchoredDialog";
import DocumentFilter from "./components/DocumentFilter";
import styles from "./DocumentsPage.module.scss";
import { CheckboxProvider } from "./components/CheckboxContext";
import DocumentsList from "./components/DocumentsList";
import DocumentsTable from "./DocumentsTable/DocumentsTable";
import { DOCUMENTS_PER_PAGE } from "./constants";
import { usePaginatedDocuments } from "./hooks/usePaginatedDocuments";
import DocumentShareDialog from "./components/DocumentShareDialog";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

const getTotalPages = ({
  isApiPaginationActive,
  currentMaxPage,
  totalPages,
  filteredDocumentsLength,
}: {
  isApiPaginationActive: boolean;
  currentMaxPage: number;
  totalPages?: number;
  filteredDocumentsLength: number;
}): number => {
  if (isApiPaginationActive) {
    return currentMaxPage;
  }

  return totalPages ?? Math.ceil(filteredDocumentsLength / DOCUMENTS_PER_PAGE);
};

// In single account mode, distinguishes accounts with no documents from filters
// that exclude them all.
const getEmptyMessageId = ({
  singleAccountMode,
  hasSelectedAccounts,
  hasDocuments,
  hasError,
}: {
  singleAccountMode: boolean;
  hasSelectedAccounts: boolean;
  hasDocuments: boolean;
  hasError: boolean;
}): string | undefined => {
  if (!singleAccountMode || hasDocuments) return undefined;
  if (!hasSelectedAccounts) return "document-select-accounts";
  return hasError ? "document-fetch-failure" : "document-empty-accounts";
};

const DocumentsPage = ({
  documentsJson,
  isHtmlJson,
  totalPages,
  singleAccountMode = false,
}: {
  documentsJson: string;
  isHtmlJson: "true" | "false";
  totalPages?: number;
  // Set by the backend view for users over the account threshold. The template ships
  // no documents; they're fetched asynchronously for the selected accounts only.
  singleAccountMode?: boolean;
}) => {
  const { m: isPastTableBreakpoint } = useBreakpoints({
    m: "(min-width: 1010px)",
  });
  const documents = JSON.parse(utils.unescape(documentsJson));
  const { l10n } = useLocalization();

  const [activePage, setActivePage] = useState(1);
  const [isTypesOpen, setIsTypesOpen] = useState(false);
  const [isYearsOpen, setIsYearsOpen] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);
  const [isShareDialogOpen, setIShareDialogOpen] = useState<boolean>(false);
  const openDocumentShareDialog = () => setIShareDialogOpen(true);
  const toggleShareDialogHandler = () => setIShareDialogOpen((prev) => !prev);
  const accounts = useAccounts();
  const internalAccounts = useMemo(
    () => accounts.filter((account) => account.source === "institution"),
    [accounts]
  );

  // Updated once a user selects accounts from the account dialog. Once updated we
  // use the selected accounts to fetch documents for those accounts only.
  // By default we fetch for the first internal account.
  const [selectedAccountIds, setSelectedAccountIds] = useState<
    API.AccountId[] | null
  >(null);
  const defaultAccountId = internalAccounts[0]?.id;
  // The account dialog resets its checkboxes when this changes.
  const requestedAccountIds = useMemo(() => {
    if (!singleAccountMode) return [];
    return selectedAccountIds ?? (defaultAccountId ? [defaultAccountId] : []);
  }, [singleAccountMode, selectedAccountIds, defaultAccountId]);
  const accountDocuments = useAccountDocuments({
    accountIds: requestedAccountIds,
  });

  const {
    ref: filtersScrollRef,
    isDragging,
    isScrollable,
    handlers,
  } = useDragHorizontalScroll<HTMLDivElement>({
    dragThresholdPx: 6,
    translateVerticalWheel: true,
    enableOnTouch: false, // keep native touch scroll
  });

  // 1. If we're using backend pagination, track accumulated documents
  const paginatedDocuments = usePaginatedDocuments(documents, () => {
    setActivePage(
      Math.trunc((filteredDocuments.length - 1) / DOCUMENTS_PER_PAGE) + 1
    );
  });
  const {
    documents: accumulatedDocuments,
    currentMaxPage,
    isApiPaginationActive,
    loadNextPage,
  } = singleAccountMode
    ? {
        documents: accountDocuments.documents,
        // The API can return more or fewer documents than we may choose to render per page, so count
        // the loaded pages. Once we hit the last one, we load the next.
        currentMaxPage: Math.max(
          1,
          Math.ceil(accountDocuments.documents.length / DOCUMENTS_PER_PAGE)
        ),
        isApiPaginationActive: accountDocuments.hasMore,
        loadNextPage: accountDocuments.loadNextPage,
      }
    : paginatedDocuments;

  // 2. Apply filters to documents
  const {
    filteredDocuments,
    filterChoices,
    activeFilters,
    setActiveFilters,
    clearFilters,
  } = useDocumentFilters<WebDocument>(accumulatedDocuments);

  // Filters only see loaded documents, so in single account mode a year or type
  // filter loads every remaining page to show all matches.
  const isLoadingAllForFilters =
    singleAccountMode &&
    isApiPaginationActive &&
    (activeFilters.years.size > 0 || activeFilters.typeLabels.size > 0);

  // 3. Paginate over documents
  const totalPageCount = getTotalPages({
    isApiPaginationActive: isApiPaginationActive && !isLoadingAllForFilters,
    currentMaxPage,
    totalPages,
    filteredDocumentsLength: filteredDocuments.length,
  });
  // Keep the active page in range when the page count drops, e.g. when legacy API
  // paging ends with filters applied.
  const currentPage = Math.min(activePage, Math.max(1, totalPageCount));
  // Keep loading while the last loaded page is active, so a short page doesn't
  // leave the pager without a page that loads more. loadNextPage changes after
  // each page loads, which re-runs this until paging ends.
  useEffect(() => {
    if (
      singleAccountMode &&
      isApiPaginationActive &&
      (isLoadingAllForFilters || currentPage >= currentMaxPage)
    ) {
      loadNextPage();
    }
  }, [
    singleAccountMode,
    isApiPaginationActive,
    isLoadingAllForFilters,
    currentPage,
    currentMaxPage,
    loadNextPage,
  ]);
  const filteredDocumentsPage = useMemo(
    () =>
      filteredDocuments.slice(
        DOCUMENTS_PER_PAGE * (currentPage - 1),
        DOCUMENTS_PER_PAGE * currentPage
      ),
    [filteredDocuments, currentPage]
  );

  // Since a single document label may reference multiple types, we need to
  // compute the number of selected document type labels.
  const selectedDocumentTypeLabels = useMemo(
    () =>
      [...activeFilters.typeLabels].reduce((knownTypeLabels, type) => {
        Object.entries(filterChoices.typeLabels).forEach(([label, value]) => {
          if (value.includes(type)) {
            knownTypeLabels.add(label);
          }
        });
        return knownTypeLabels;
      }, new Set<string>()),
    [activeFilters.typeLabels, filterChoices.typeLabels]
  );

  const documentTypeItems = Object.entries(filterChoices.typeLabels).map(
    ([label, value]) => ({
      key: value,
      name: label,
    })
  );
  const documentTypeContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-type-popover-heading",
        null,
        "Document"
      )}
      activeFilters={[...activeFilters.typeLabels]}
      items={documentTypeItems}
      onApply={(keys) => {
        setActiveFilters((prev) => ({ ...prev, typeLabels: new Set(keys) }));
        setIsTypesOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsTypesOpen}
      isOpen={isTypesOpen}
      trigger={
        <DocumentFilter
          kind="type"
          count={selectedDocumentTypeLabels.size || undefined}
          onClick={() => setIsTypesOpen(true)}
        />
      }
    />
  );

  const accountNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    Object.entries(filterChoices.accounts).forEach(([id, name]) => {
      const account = accounts.find((acc) => acc.id === id);
      lookup[id] = account?.getShortDescription(false) || name;
    });
    return lookup;
  }, [filterChoices.accounts, accounts]);

  const documentAccountItems = Object.entries(filterChoices.accounts).map(
    ([id, name]) => {
      return {
        key: id,
        name: accountNameLookup[id] ?? name,
      };
    }
  );

  // In single account mode the account filter chooses which accounts'
  // documents to fetch, rather than filtering documents already loaded.
  const documentAccountContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-account-popover-heading",
        null,
        "Account"
      )}
      activeFilters={
        singleAccountMode ? requestedAccountIds : [...activeFilters.accounts]
      }
      items={
        singleAccountMode
          ? internalAccounts.map((account) => ({
              key: account.id,
              name: account.getShortDescription(false),
            }))
          : documentAccountItems
      }
      onApply={(keys) => {
        if (singleAccountMode) {
          setSelectedAccountIds(keys as API.AccountId[]);
        } else {
          setActiveFilters((prev) => ({ ...prev, accounts: new Set(keys) }));
        }
        setIsAccountsOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsAccountsOpen}
      isOpen={isAccountsOpen}
      trigger={
        <DocumentFilter
          kind="account"
          count={
            (singleAccountMode
              ? requestedAccountIds.length
              : activeFilters.accounts.size) || undefined
          }
          onClick={() => setIsAccountsOpen(true)}
        />
      }
    />
  );

  const documentYearContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-year-popover-heading",
        null,
        "Year"
      )}
      activeFilters={[...activeFilters.years]}
      items={filterChoices.years.map((year) => ({
        key: year,
        name: year,
      }))}
      onApply={(keys) => {
        setActiveFilters((prev) => ({ ...prev, years: new Set(keys) }));
        setIsYearsOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsYearsOpen}
      isOpen={isYearsOpen}
      trigger={
        <DocumentFilter
          kind="year"
          count={activeFilters.years.size || undefined}
          onClick={() => setIsYearsOpen(true)}
        />
      }
    />
  );

  const WrapperElement = isPastTableBreakpoint ? "div" : ContentCard;
  // Later pages may still hold matches, so don't show an empty state yet.
  const isLoading =
    accountDocuments.isLoading ||
    (isLoadingAllForFilters && filteredDocuments.length === 0);

  const emptyMessageId = getEmptyMessageId({
    singleAccountMode,
    hasSelectedAccounts: requestedAccountIds.length > 0,
    hasDocuments: accumulatedDocuments.length > 0,
    hasError: accountDocuments.hasError,
  });

  const filtersRowClassName = cc([
    styles["documents-page-filters-row"],
    isDragging && styles.dragging,
  ]);

  return (
    <PageLayout
      maxWidth="l"
      header={
        <div className={styles.header}>
          <h2>{l10n.getString("heading-statements")}</h2>
        </div>
      }
    >
      <CheckboxProvider>
        <div className={styles["documents-page-filters"]}>
          <div
            className={filtersRowClassName}
            ref={filtersScrollRef}
            {...handlers}
          >
            <Row gapSize="xs" alignItems="center">
              {documentTypeContent}
              {documentAccountContent}
              {documentYearContent}
              {(activeFilters.typeLabels.size > 0 ||
                activeFilters.years.size > 0 ||
                activeFilters.accounts.size > 0) && (
                <button
                  onClick={() => {
                    setActivePage(1);
                    clearFilters();
                  }}
                  className={styles["documents-page-clear-all-button"]}
                  data-testid="documents-clear-all-button"
                >
                  <Localized id="document-filter-clear-all">
                    Clear all
                  </Localized>
                </button>
              )}
              {isScrollable && (
                <>
                  <div
                    className={
                      styles["documents-page-filters-row-gradient-spacer"]
                    }
                  />
                  <div
                    className={styles["documents-page-filters-row-gradient"]}
                  />
                </>
              )}
            </Row>
          </div>
        </div>
        <WrapperElement kind="bordered">
          {isLoading ? (
            <LoadingShim isLoading testId="documents-loading">
              <div className={styles["documents-page-loading"]} />
            </LoadingShim>
          ) : null}
          {!isLoading && isPastTableBreakpoint ? (
            <DocumentsTable
              documents={filteredDocumentsPage}
              isHtmlJson={isHtmlJson === "true"}
              openDocumentShareDialog={openDocumentShareDialog}
              emptyMessageId={emptyMessageId}
            />
          ) : null}
          {!isLoading && !isPastTableBreakpoint ? (
            <DocumentsList
              documents={filteredDocumentsPage}
              isHtmlJson={isHtmlJson === "true"}
              emptyMessageId={emptyMessageId}
            />
          ) : null}

          <Pagination
            testId="documents-pagination"
            totalPages={totalPageCount}
            selectedPage={currentPage}
            onPageChange={(newPage: number) => {
              // If we're using backend pagination and we've navigated to a page that is at or past our
              // current max page, attempt to load the next page of documents.
              if (isApiPaginationActive && newPage >= currentMaxPage) {
                loadNextPage();
              }
              setActivePage(newPage);
            }}
          />

          <DocumentShareDialog
            isOpen={isShareDialogOpen}
            onUserDismiss={toggleShareDialogHandler}
          />
        </WrapperElement>
      </CheckboxProvider>
    </PageLayout>
  );
};

export default DocumentsPage;
